import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle, ImageSquare, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import ContextHelpButton from '../ContextHelpButton';

const STARTERS = [
  { slotKey: 'photo_cover', label: 'Cover con foto', hint: 'Slide di apertura con fotografia.' },
  { slotKey: 'cover_graph', label: 'Cover senza foto', hint: 'Slide di apertura puramente grafica o testuale.' },
  { slotKey: 'editorial', label: 'Slide editoriale / testo', hint: 'Slide generica prevalentemente testuale.' },
  { slotKey: 'editorial_list', label: 'Lista', hint: 'Slide con elenco, punti o elementi ripetuti.' },
  { slotKey: 'cta', label: 'CTA', hint: 'Call to action, domanda o engagement.' },
  { slotKey: 'endpage', label: 'Chiusura', hint: 'Slide finale / closing.' },
];

export default function WizardDesignDnaV2Step({ api, projectId, onBack, onContinue, onDnaChange }) {
  const { t } = useTranslation();
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState('');

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/design/v3/references`);
      const list = data?.references || [];
      setReferences(list);
      onDnaChange?.({ references: list });
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setLoading(false);
    }
  }, [api, projectId, onDnaChange]);

  useEffect(() => { void load(); }, [load]);

  const bySlot = useMemo(() => new Map(references.map(item => [item.slot_key, item])), [references]);

  const uploadReference = async (starter, file) => {
    if (!starter?.slotKey || !file) return;
    const form = new FormData();
    form.append('file', file);
    form.append('slot_key', starter.slotKey);
    setUploadingId(starter.slotKey);
    try {
      await api.post(`/projects/${projectId}/design/v3/references`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
      toast.success(`${starter.label}: reference salvata`);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setUploadingId('');
    }
  };

  return (
    <div data-testid="wizard-design-v3-step">
      <span data-testid="wizard-design-dna-v2-step" className="hidden" aria-hidden="true" />
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold">Design DNA</h2>
          <span className="badge purple">V3</span>
        </div>
        <ContextHelpButton articleId="design-dna-overview" label="Guida Design DNA" />
      </div>
      <p className="text-[var(--text-secondary)] mb-6 text-sm max-w-3xl">
        Carica solo le immagini di riferimento per i sei archetipi iniziali. Qui non si modifica il layout: designer, font, colori, logo e separatori restano esclusivamente nella sezione Design del progetto.
      </p>

      {loading && references.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)] mb-6">Carico gli archetipi…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-6" data-testid="wizard-v3-reference-grid">
          {STARTERS.map(starter => {
            const reference = bySlot.get(starter.slotKey);
            const busy = uploadingId === starter.slotKey;
            return (
              <div key={starter.slotKey} className="rounded-2xl border border-[var(--border-color)] bg-[rgba(255,255,255,0.03)] overflow-hidden" data-testid={`wizard-v3-reference-${starter.slotKey}`}>
                <div className="aspect-[4/5] bg-black/20 flex items-center justify-center overflow-hidden">
                  {reference?.url
                    ? <img src={reference.url} alt={`${starter.label} reference`} className="w-full h-full object-contain" />
                    : <ImageSquare size={34} className="opacity-40" />}
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold">{starter.label}</p>
                      <p className="text-[10px] text-[var(--text-muted)] mt-1">{starter.hint}</p>
                    </div>
                    {reference?.url ? <CheckCircle size={17} weight="fill" className="text-emerald-400 shrink-0" /> : null}
                  </div>
                  <label className={`btn-ghost text-[11px] w-full justify-center mt-3 cursor-pointer ${busy ? 'opacity-60 pointer-events-none' : ''}`}>
                    <UploadSimple size={13} /> {busy ? 'Caricamento…' : reference?.url ? 'Sostituisci reference' : 'Carica reference'}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/png,image/jpeg,image/webp"
                      disabled={busy}
                      onChange={event => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        void uploadReference(starter, file);
                      }}
                    />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-[var(--text-muted)] mb-6">
        Le reference sono facoltative e restano nella libreria Reference del progetto. Nel Design potrai scegliere quale reference affiancare a ciascun Template V3.
      </p>

      <div className="flex justify-between gap-3">
        <button className="btn-ghost" type="button" onClick={onBack}>
          <ArrowLeft size={16} /> {t('wizard.actions.back')}
        </button>
        <button className="btn-gradient" type="button" onClick={onContinue}>
          {t('wizard.actions.continue')} <ArrowRight weight="bold" size={18} />
        </button>
      </div>
    </div>
  );
}
